import type { ReactNode } from 'react';
import { Check, Minus, Star } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

/**
 * Structured data table for financial / factual comparisons.
 *
 * Two renderings from one data source:
 *   - >=md: a real <table> so the data is machine-readable and copyable.
 *   - <md:  stacked cards, because a 5-column fee table is unreadable at 375px
 *           and horizontal scroll tanks mobile engagement.
 *
 * Numeric cells use `tabular-nums` so figures line up column-wise.
 */

export interface TableColumn {
  key: string;
  /** English column header. */
  header: string;
  /** Optional Korean header, stacked beneath. */
  headerKo?: string;
  /** Right-align + tabular numerals. Use for money, rates, durations. */
  numeric?: boolean;
}

export interface TableRow {
  /** Row label — the thing being compared (e.g. "WireBarley"). */
  label: string;
  labelKo?: string;
  /** Small note under the label (e.g. "Korean-owned, US-licensed MSB"). */
  note?: string;
  /** Flags this row as the editorial pick. */
  highlight?: boolean;
  highlightLabel?: string;
  /** Row figures come from the live rate feed rather than the static baseline. */
  live?: boolean;
  cells: Record<string, ReactNode | boolean>;
}

/** Small pulse marking a row whose figures are refreshed from the rate feed. */
function LiveDot() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700 dark:bg-green-900/20 dark:text-green-400"
      title="Updated from live exchange rates"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-600" />
      </span>
      Live
    </span>
  );
}

interface ComparisonTableProps {
  caption: string;
  captionKo?: string;
  columns: TableColumn[];
  rows: TableRow[];
  /** Rendered under the table — required for any table carrying money figures. */
  footnote?: ReactNode;
  className?: string;
}

function Cell({ value }: { value: ReactNode | boolean }) {
  if (value === true) {
    return <Check className="inline h-4 w-4 text-green-600 dark:text-green-500" aria-label="Yes" />;
  }
  if (value === false) {
    return <Minus className="inline h-4 w-4 text-ink-400" aria-label="No" />;
  }
  return <>{value}</>;
}

export default function ComparisonTable({
  caption,
  captionKo,
  columns,
  rows,
  footnote,
  className,
}: ComparisonTableProps) {
  return (
    <figure className={twMerge('my-10', className)}>
      {/* ── Desktop / tablet: real table ── */}
      <div className="hidden overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card md:block">
        <table className="w-full border-collapse text-left text-sm">
          <caption className="border-b border-ink-200 bg-ink-50 px-5 py-4 text-left dark:border-surface-dark-border dark:bg-white/[0.03]">
            <span className="block text-base font-bold text-ink-900 dark:text-ink-50">{caption}</span>
            {captionKo && (
              <span lang="ko" className="mt-0.5 block font-korean text-sm text-ink-500 dark:text-ink-400">
                {captionKo}
              </span>
            )}
          </caption>

          <thead>
            <tr className="border-b border-ink-200 dark:border-surface-dark-border">
              <th scope="col" className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                Provider
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={twMerge(
                    'px-5 py-3 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400',
                    col.numeric && 'text-right'
                  )}
                >
                  {col.header}
                  {col.headerKo && (
                    <span lang="ko" className="mt-0.5 block font-korean text-[11px] font-normal normal-case tracking-normal text-ink-400">
                      {col.headerKo}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row.label}
                className={twMerge(
                  'border-b border-ink-100 last:border-0 dark:border-surface-dark-border/60',
                  row.highlight && 'bg-hwang-50/60 dark:bg-hwang-500/[0.07]'
                )}
              >
                <th scope="row" className="px-5 py-4 align-top font-semibold text-ink-900 dark:text-ink-50">
                  <span className="flex flex-wrap items-center gap-2">
                    {row.label}
                    {row.highlight && (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-hwang-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-heuk-500">
                        <Star className="h-2.5 w-2.5 fill-current" aria-hidden="true" />
                        {row.highlightLabel ?? 'Best value'}
                      </span>
                    )}
                    {row.live && <LiveDot />}
                  </span>
                  {row.labelKo && (
                    <span lang="ko" className="mt-0.5 block font-korean text-xs font-normal text-ink-500 dark:text-ink-400">
                      {row.labelKo}
                    </span>
                  )}
                  {row.note && (
                    <span className="mt-1 block text-xs font-normal leading-relaxed text-ink-500 dark:text-ink-400">
                      {row.note}
                    </span>
                  )}
                </th>

                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={twMerge(
                      'px-5 py-4 align-top text-ink-700 dark:text-ink-200',
                      col.numeric && 'text-right font-mono tabular-nums'
                    )}
                  >
                    <Cell value={row.cells[col.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile: stacked cards ── */}
      <div className="space-y-3 md:hidden">
        <div className="px-1">
          <h4 className="text-base font-bold text-ink-900 dark:text-ink-50">{caption}</h4>
          {captionKo && (
            <p lang="ko" className="mt-0.5 font-korean text-sm text-ink-500 dark:text-ink-400">
              {captionKo}
            </p>
          )}
        </div>

        {rows.map((row) => (
          <div
            key={row.label}
            className={twMerge(
              'rounded-xl border bg-white p-4 shadow-sm dark:bg-surface-dark-card',
              row.highlight
                ? 'border-hwang-500 dark:border-hwang-500/60'
                : 'border-ink-200 dark:border-surface-dark-border'
            )}
          >
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-ink-900 dark:text-ink-50">{row.label}</span>
              {row.highlight && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-hwang-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-heuk-500">
                  <Star className="h-2.5 w-2.5 fill-current" aria-hidden="true" />
                  {row.highlightLabel ?? 'Best value'}
                </span>
              )}
              {row.live && <LiveDot />}
            </div>
            {row.note && (
              <p className="-mt-2 mb-3 text-xs leading-relaxed text-ink-500 dark:text-ink-400">{row.note}</p>
            )}

            <dl className="space-y-2">
              {columns.map((col) => (
                <div key={col.key} className="flex items-start justify-between gap-4 text-sm">
                  <dt className="text-ink-500 dark:text-ink-400">{col.header}</dt>
                  <dd
                    className={twMerge(
                      'text-right font-medium text-ink-900 dark:text-ink-50',
                      col.numeric && 'font-mono tabular-nums'
                    )}
                  >
                    <Cell value={row.cells[col.key]} />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {footnote && (
        <figcaption className="mt-3 px-1 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
          {footnote}
        </figcaption>
      )}
    </figure>
  );
}
